import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import {
  TWizardStepComponentRef,
  TWizardStepComponentEditable,
  WizardStepComponentType,
  TWizardStepMultiEditableProps,
  StepMultiEditableModes
} from "../../../interfaces";
import styled from "styled-components/macro";
import { DefaultFont } from "../../../../../styles/fonts";
import { LoadingIndicator } from "../../../../LoadingIndicator";
import { useTranslation } from "react-i18next";
import { TextButton } from "../../styles";

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;
const StepContainer = styled.div`
  padding: 32px;
  border: 1px solid #dcd6ce;
  border-radius: 4px;
  background: white;

  position: relative;
`;
const StepBlockedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  background: rgba(255, 255, 255, 0.7);

  display: flex;
  align-items: center;
  justify-content: center;
`;
const ButtonsContainer = styled.div`
  margin-left: 10px;
`;

const StepHeader = styled.div`
  display: flex;
  margin-bottom: 32px;
  justify-content: space-between;
`;

const Title = styled.div`
  ${DefaultFont};
  font-size: 24px;
  color: ${(p) => p.theme.colors.hardGray};
`;

export const WizardStepOverview = forwardRef(
  <TState, TStep extends string>(
    {
      state,
      onEditModeChange,
      onStateChange,
      stepsOrder,
      stepsConfiguration,
      getStepTitle
    }: TWizardStepMultiEditableProps<TState, TStep>,
    ref: React.ForwardedRef<TWizardStepComponentRef<TState>>
  ) => {
    const stepsRefs = useRef<{
      [key in TStep]?: TWizardStepComponentRef<TState> | null;
    }>({});
    const [stepsIsLoading, setStepsIsLoading] = useState<{
      [key in TStep]?: boolean;
    }>({});
    const [editStepName, setEditStepName] = useState<null | TStep>();

    const { t: tw } = useTranslation("wizard");

    useImperativeHandle(ref, () => ({
      save: async () => {
        return state;
      },
      savePartially: async () => {
        return state;
      }
    }));

    return (
      <Root>
        {stepsOrder
          .filter((stepName) => {
            const stepConfiguration = stepsConfiguration[stepName]!;
            if (!stepConfiguration) {
              throw Error(`no stepConfiguration stepName:${stepName}`);
            }
            const { type } = stepConfiguration;
            return (
              type === WizardStepComponentType.Editable &&
              stepConfiguration.showInOverview !== false
            );
          })
          .map((stepName) => {
            const stepConfiguration = stepsConfiguration[stepName]!;

            return (
              <StepContainer key={stepName}>
                <StepHeader>
                  <Title>{getStepTitle(stepName)}</Title>
                  <ButtonsContainer>
                    {editStepName === stepName ? (
                      <TextButton
                        onClick={() => {
                          const saveMethod = stepsRefs.current[stepName]?.save;
                          if (!saveMethod) {
                            return;
                          }
                          setStepsIsLoading((prev) => ({
                            ...prev,
                            [stepName]: true
                          }));
                          saveMethod()
                            .then((state) => {
                              onStateChange(state as TState);
                              setEditStepName(null);
                              onEditModeChange(StepMultiEditableModes.View);
                            })
                            .finally(() => {
                              setStepsIsLoading((prev) => ({
                                ...prev,
                                [stepName]: false
                              }));
                            });
                        }}
                      >
                        {tw("step.overview.save")}
                      </TextButton>
                    ) : (
                      <TextButton
                        onClick={() => {
                          setEditStepName(stepName);
                          onEditModeChange(StepMultiEditableModes.Edit);
                        }}
                        disabled={Boolean(editStepName)}
                      >
                        {tw("step.overview.edit")}
                      </TextButton>
                    )}
                  </ButtonsContainer>
                </StepHeader>

                {React.createElement(
                  stepConfiguration.component as TWizardStepComponentEditable<TState>,
                  {
                    state: state,
                    viewonly:
                      editStepName !== stepName || stepsIsLoading[stepName],
                    ref: (ref) => {
                      stepsRefs.current[stepName] = ref;
                    }
                  }
                )}

                {editStepName && editStepName !== stepName ? (
                  <StepBlockedOverlay />
                ) : null}
                {editStepName === stepName && stepsIsLoading[stepName] ? (
                  <StepBlockedOverlay>
                    <LoadingIndicator fontSize={30} />
                  </StepBlockedOverlay>
                ) : null}
              </StepContainer>
            );
          })}
      </Root>
    );
  }
);
